import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createSavedReportsClient } from './api';
import {
  SavedReportSource,
  type ReportSchedulePayload,
  type SaveReportConfigPayload,
} from './model';

export const savedReportsQueryKey = ['reporting', 'saved-reports'] as const;
export const ownedSavedReportsQueryKey = [...savedReportsQueryKey, 'owned'] as const;
export const sharedSavedReportsQueryKey = [...savedReportsQueryKey, 'shared'] as const;
export const scheduledReportsQueryKey = [...savedReportsQueryKey, 'scheduled'] as const;

function useSavedReportsClient(agencyId?: string) {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return {
    client: createSavedReportsClient({
      config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
      accessToken: session.accessToken,
      agencyId,
    }),
    scopedKey: (key: readonly string[]) => (agencyId ? [...key, config.networkId, agencyId] : key),
  };
}

export function useOwnedSavedReportsQuery(enabled = true, agencyId?: string) {
  const { client, scopedKey } = useSavedReportsClient(agencyId);
  return useQuery({
    queryKey: scopedKey(ownedSavedReportsQueryKey),
    queryFn: () => client.listOwned(),
    enabled,
  });
}

export function useSharedSavedReportsQuery(enabled = true, agencyId?: string) {
  const { client, scopedKey } = useSavedReportsClient(agencyId);
  return useQuery({
    queryKey: scopedKey(sharedSavedReportsQueryKey),
    queryFn: () => client.listShared(),
    enabled,
  });
}

export function useScheduledReportsQuery(enabled = true, agencyId?: string) {
  const { client, scopedKey } = useSavedReportsClient(agencyId);
  return useQuery({
    queryKey: scopedKey(scheduledReportsQueryKey),
    queryFn: () => client.listScheduled(),
    enabled,
  });
}

export function useSavedReportQuery(
  id: string | undefined,
  source: SavedReportSource | undefined,
  agencyId?: string,
) {
  const owned = useOwnedSavedReportsQuery(
    Boolean(id) && source === SavedReportSource.Owned,
    agencyId,
  );
  const shared = useSharedSavedReportsQuery(
    Boolean(id) && source === SavedReportSource.Shared,
    agencyId,
  );
  const query = source === SavedReportSource.Shared ? shared : owned;
  return {
    ...query,
    data: id ? query.data?.find((report) => report.id === id) : undefined,
  };
}

export function useSavedReportMutations(agencyId?: string) {
  const { client, scopedKey } = useSavedReportsClient(agencyId);
  const queryClient = useQueryClient();
  const invalidateReportCollections = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: scopedKey(ownedSavedReportsQueryKey) }),
      queryClient.invalidateQueries({ queryKey: scopedKey(sharedSavedReportsQueryKey) }),
      queryClient.invalidateQueries({ queryKey: scopedKey(scheduledReportsQueryKey) }),
    ]);
  const invalidateSchedules = () =>
    queryClient.invalidateQueries({ queryKey: scopedKey(scheduledReportsQueryKey) });

  return {
    create: useMutation({
      mutationFn: (payload: SaveReportConfigPayload) => client.create(payload),
      onSuccess: invalidateReportCollections,
    }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: SaveReportConfigPayload }) =>
        client.update(id, payload),
      onSuccess: invalidateReportCollections,
    }),
    delete: useMutation({
      mutationFn: (id: string) => client.delete(id),
      onSuccess: invalidateReportCollections,
    }),
    createSchedule: useMutation({
      mutationFn: ({ reportId, payload }: { reportId: string; payload: ReportSchedulePayload }) =>
        client.createSchedule(reportId, payload),
      onSuccess: invalidateSchedules,
    }),
    deleteSchedule: useMutation({
      mutationFn: (id: string) => client.deleteSchedule(id),
      onSuccess: invalidateSchedules,
    }),
    updateSchedule: useMutation({
      mutationFn: ({
        scheduleId,
        payload,
      }: {
        scheduleId: string;
        payload: ReportSchedulePayload;
      }) => client.updateSchedule(scheduleId, payload),
      onSuccess: invalidateSchedules,
    }),
  };
}

export function useReportShareUserOptions(enabled = true, agencyId?: string) {
  const { client, scopedKey } = useSavedReportsClient(agencyId);
  return useQuery({
    queryKey: scopedKey([...savedReportsQueryKey, 'share-users', 'all']),
    queryFn: () => client.listShareUsers(),
    enabled,
  });
}
